// ============================================================
// SurveyCycleCard.js — CareVoice cycle config per client.
// Fields: intervalMonths (3/6/12), startMonth (1-12 or no preference),
// lowResponseThreshold (>= 1), freeTextRetentionDays (1-365 or the
// platform default).
//
// startMonth is a CREATE-GATE on the client's FIRST cycle (the cycle
// timer waits for that month), so "No preference" is a real choice and
// must round-trip as null. Until 2026-10-01 the GET always answered 1, so
// this card loaded January and a save wrote it back.
//
// freeTextRetentionDays decides when survey free text is deleted (the
// careVoiceFreeText retention policy). Blank is not a value: the admin
// picks "platform default" (sent as null) or a custom number of days.
// The default and the bounds come from the GET, never a copy here.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Field from '../../components/Field';
import { toast } from '../../components/Toaster';
import {
  getClientSurveyCycleConfig, patchClientSurveyCycleConfig,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' },   { value: 5, label: 'May' },      { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },   { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

export default function SurveyCycleCard({ clientId }) {
  const [form, setForm] = useState({
    intervalMonths: 6, startMonth: null, lowResponseThreshold: 5,
    retentionMode: 'default', retentionDays: '',
  });
  const [limits, setLimits] = useState({ defaultDays: 90, minDays: 1, maxDays: 365 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [retentionError, setRetentionError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientSurveyCycleConfig(clientId);
      const stored = res.freeTextRetentionDays;
      setForm({
        intervalMonths: res.intervalMonths,
        startMonth: res.startMonth ?? null,
        lowResponseThreshold: res.lowResponseThreshold,
        retentionMode: stored == null ? 'default' : 'custom',
        retentionDays: stored == null ? '' : String(stored),
      });
      setLimits({
        defaultDays: res.freeTextRetentionDefaultDays ?? 90,
        minDays: res.freeTextRetentionMinDays ?? 1,
        maxDays: res.freeTextRetentionMaxDays ?? 365,
      });
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  async function submit(e) {
    e.preventDefault();
    let freeTextRetentionDays = null;
    if (form.retentionMode === 'custom') {
      const n = Number(form.retentionDays);
      if (!Number.isInteger(n) || n < limits.minDays || n > limits.maxDays) {
        setRetentionError(`Enter a whole number of days from ${limits.minDays} to ${limits.maxDays}.`);
        return;
      }
      freeTextRetentionDays = n;
    }
    setRetentionError(null);
    setSaving(true);
    try {
      await patchClientSurveyCycleConfig(clientId, {
        intervalMonths: form.intervalMonths,
        startMonth: form.startMonth,
        lowResponseThreshold: form.lowResponseThreshold,
        freeTextRetentionDays,
      });
      toast.success('Survey cycle config saved');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setSaving(false); }
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <form className="pc-form" onSubmit={submit}>
      <Field label="Start Month" help="The first survey cycle waits for this month. Choose No preference to start at the next cycle run.">
        <select
          value={form.startMonth == null ? '' : form.startMonth}
          onChange={(e) => setForm({ ...form, startMonth: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
        >
          <option value="">No preference</option>
          {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </Field>
      <Field label="Interval" required>
        <select value={form.intervalMonths} onChange={(e) => setForm({ ...form, intervalMonths: parseInt(e.target.value, 10) })}>
          <option value={3}>3 months</option>
          <option value={6}>6 months</option>
          <option value={12}>12 months</option>
        </select>
      </Field>
      <Field label="Low-Response Threshold" required help="Minimum responses before the cycle's analytics are considered representative.">
        <input
          type="number"
          min={1}
          value={form.lowResponseThreshold}
          onChange={(e) => setForm({ ...form, lowResponseThreshold: parseInt(e.target.value, 10) || 1 })}
          required
        />
      </Field>

      <Field label="Keep survey comments for">
        <select
          value={form.retentionMode}
          onChange={(e) => { setRetentionError(null); setForm({ ...form, retentionMode: e.target.value }); }}
        >
          <option value="default">Platform default ({limits.defaultDays} days)</option>
          <option value="custom">A set number of days</option>
        </select>
      </Field>
      {form.retentionMode === 'custom' && (
        <Field
          label="Days after the cycle closes"
          required
          error={retentionError}
          help={`From ${limits.minDays} to ${limits.maxDays} days.`}
        >
          <input
            type="number"
            min={limits.minDays}
            max={limits.maxDays}
            step={1}
            value={form.retentionDays}
            onChange={(e) => { setRetentionError(null); setForm({ ...form, retentionDays: e.target.value }); }}
            required
          />
        </Field>
      )}
      <div className="pc-explain" data-testid="retention-explainer">
        <p>
          After this many days, what each person wrote in a survey cycle can be deleted. Nothing is
          deleted automatically: an A2Z operator reviews and confirms each deletion.
        </p>
        <p><strong>Kept:</strong> the themes, response counts, Quality Statement links, recommended actions and every score.</p>
        <p><strong>Deleted:</strong> the comments themselves, including any quotes. They cannot be recovered.</p>
        <p>A cycle is only deleted after its analysis has been run, so the themes are always saved first.</p>
      </div>

      <div className="pc-actions">
        <button type="submit" className="pc-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
